/**
 * Dimension Calculation Utilities
 *
 * Auto-calculate garden dimensions from polygon outline vertices.
 * Used in Wizard Step 3 to derive length, width, and area from the drawn garden boundary.
 */

/**
 * Calculate bounding box dimensions from polygon vertices
 *
 * Returns the minimum and maximum X/Y coordinates, along with width and height.
 * This represents the smallest rectangle that can contain the entire polygon.
 *
 * @param {Array} vertices - Polygon vertices [{x, y}, ...]
 * @returns {object} Bounding box { minX, maxX, minY, maxY, width, height }
 */
export const calculateBoundingBox = (vertices) => {
  if (!vertices || vertices.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 };
  }

  const xCoords = vertices.map(v => v.x);
  const yCoords = vertices.map(v => v.y);

  const minX = Math.min(...xCoords);
  const maxX = Math.max(...xCoords);
  const minY = Math.min(...yCoords);
  const maxY = Math.max(...yCoords);

  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  };
};

/**
 * Convert pixel measurement to meters using calibration scale
 *
 * Uses the calibration scale from Wizard Step 2 (background image calibration).
 * Falls back to 100px = 1m (1px = 1cm) if no calibration is provided.
 *
 * @param {number} pixels - Measurement in pixels
 * @param {number} scale - Calibration scale (pixels per meter)
 * @returns {number} Measurement in meters
 */
export const convertPixelsToMeters = (pixels, scale) => {
  if (!scale || scale <= 0) {
    // Fallback: 100px = 1m (1px = 1cm)
    scale = 100;
  }
  return pixels / scale;
};

/**
 * Calculate polygon area using the Shoelace formula.
 * Pure math utility — no Fabric.js dependency.
 *
 * @param {Array} vertices - [{x, y}, ...]
 * @returns {number} Area in square pixels
 */
export const calculatePolygonArea = (vertices) => {
  if (!vertices || vertices.length < 3) return 0;
  let area = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += vertices[i].x * vertices[j].y;
    area -= vertices[j].x * vertices[i].y;
  }
  return Math.abs(area / 2);
};

/**
 * Calculate all dimensions from polygon outline
 *
 * This is the master function that orchestrates all dimension calculations:
 * - Bounding box (width x length in meters)
 * - Actual polygon area (Shoelace formula in m²)
 * - Applies calibration scale to convert pixels to meters
 *
 * @param {Array} vertices - Polygon vertices [{x, y}, ...]
 * @param {number} calibrationScale - Pixels per meter (from Step 2)
 * @param {function} [calcAreaFn] - Optional Shoelace override (defaults to built-in)
 * @returns {object} Calculated dimensions with all metadata
 */
export const calculateDimensionsFromOutline = (
  vertices,
  calibrationScale,
  calcAreaFn
) => {
  if (!vertices || vertices.length < 3) {
    return {
      widthMeters: 0,
      lengthMeters: 0,
      areaSquareMeters: 0,
      calibrationScale: calibrationScale || 100,
      widthPixels: 0,
      lengthPixels: 0,
      areaSquarePixels: 0
    };
  }

  // Use provided scale or fallback to 100px/m (1px = 1cm)
  const scale = calibrationScale && calibrationScale > 0 ? calibrationScale : 100;

  // Calculate bounding box (in pixels)
  const bbox = calculateBoundingBox(vertices);

  // Calculate polygon area using Shoelace formula (in pixels²)
  const areaPx = (calcAreaFn || calculatePolygonArea)(vertices);

  // Convert to meters
  const widthM = bbox.width / scale;
  const lengthM = bbox.height / scale;
  const areaM2 = areaPx / (scale * scale);

  return {
    // Calibrated dimensions in meters (rounded to 2 decimal places)
    widthMeters: parseFloat(widthM.toFixed(2)),
    lengthMeters: parseFloat(lengthM.toFixed(2)),
    areaSquareMeters: parseFloat(areaM2.toFixed(2)),

    // Metadata
    calibrationScale: scale,

    // Raw pixel values (for debugging/reference)
    widthPixels: bbox.width,
    lengthPixels: bbox.height,
    areaSquarePixels: areaPx
  };
};
